import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { CalendarClock, Check, Clock, Loader2, Phone, UserRound, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import { Reveal, Eyebrow } from "@/components/site/Motion";
import { Field } from "@/components/site/QuoteModal";
import { http, formatApiError, PHONE, PHONE_HREF } from "@/lib/api";

const PERKS = [
  [Clock, "30 minutes, no pressure", "A quick chat about how you take payments today."],
  [UserRound, "Talk to a real person", "Meet the account manager who'll look after you."],
  [CalendarClock, "Mon\u2013Fri, 9am\u20135pm ET", "Pick the time that works for your business."],
];

const DayPicker = ({ days, day, onPick }) => (
  <div className="flex gap-2 overflow-x-auto pb-2" data-lenis-prevent>
    {days.map((d, i) => (
      <button key={d.date} type="button" onClick={() => onPick(i)} data-testid={`booking-day-${d.date}`}
        className={`shrink-0 rounded-2xl border px-4 py-3 text-center transition-[background-color,border-color,transform] duration-300 hover:-translate-y-0.5 ${i === day ? "border-[#0B2A6F] bg-[#0B2A6F] text-white" : "border-emerald-900/10 bg-white text-[#050B1F] hover:border-emerald-500"}`}>
        <span className="block font-mono text-[11px] uppercase tracking-[0.15em] opacity-80">{d.weekday}</span>
        <span className="block font-display text-lg font-bold">{d.label}</span>
      </button>
    ))}
  </div>
);

const TimeGrid = ({ slots, slot, onPick }) => (
  <div className="grid grid-cols-3 gap-2 sm:grid-cols-4">
    {slots.map((s) => (
      <button key={s.slot} type="button" onClick={() => onPick(s)} data-testid={`booking-time-${s.time.replace(/[: ]/g, "")}`}
        className={`rounded-xl border px-2 py-2.5 text-sm font-semibold transition-[background-color,border-color] duration-200 ${slot?.slot === s.slot ? "border-lime-400 bg-lime-400 text-[#050B1F]" : "border-emerald-900/10 bg-white hover:border-lime-400"}`}>
        {s.time}
      </button>
    ))}
  </div>
);

const Done = ({ label, onReset }) => (
  <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className="py-10 text-center" data-testid="booking-success">
    <span className="mx-auto grid h-16 w-16 place-items-center rounded-full bg-lime-400 text-[#050B1F]"><Check className="h-8 w-8" /></span>
    <h3 className="mt-6 font-display text-3xl font-extrabold">You're booked.</h3>
    <p className="mt-2 text-emerald-950/75">We'll call you on <span className="font-semibold text-[#050B1F]">{label}</span>.</p>
    <button onClick={onReset} data-testid="booking-reset-btn" className="mt-6 text-sm font-semibold text-emerald-700 underline underline-offset-4">Book another time</button>
  </motion.div>
);

export const BookCall = () => {
  const [days, setDays] = useState(null);
  const [day, setDay] = useState(0);
  const [slot, setSlot] = useState(null);
  const [form, setForm] = useState({ name: "", email: "", phone: "", business: "", message: "" });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const load = () => {
    http.get("/booking/slots").then((r) => setDays(Array.isArray(r.data?.days) ? r.data.days : [])).catch(() => setDays([]));
  };
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await http.post("/booking", { ...form, slot: slot.slot });
      setDone(data?.slot_label ?? "your chosen time");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
      setSlot(null);
      load();
    } finally { setBusy(false); }
  };
  const reset = () => { setDone(null); setSlot(null); setForm({ name: "", email: "", phone: "", business: "", message: "" }); load(); };
  const current = days?.[day];

  return (
    <section id="book-call" className="relative bg-white px-4 py-24 sm:px-6 lg:px-12 lg:py-32" data-testid="book-call-section">
      <div className="mx-auto grid max-w-7xl gap-12 lg:grid-cols-12">
        <Reveal className="min-w-0 lg:col-span-5">
          <Eyebrow>Book a call</Eyebrow>
          <h2 className="mt-4 font-display text-4xl font-extrabold leading-[1.02] tracking-tight sm:text-5xl lg:text-6xl">
            Pick a time. <span className="text-emerald-600">We'll call you.</span>
          </h2>
          <p className="mt-5 max-w-md text-base text-emerald-950/75 md:text-lg">Choose a 30-minute slot and your account manager will walk you through eliminating your transaction fees.</p>
          <div className="mt-10 space-y-4">
            {PERKS.map(([I, t, d]) => (
              <div key={t} className="flex items-start gap-4">
                <span className="grid h-11 w-11 shrink-0 place-items-center rounded-2xl bg-[#EAF3FF] text-emerald-700"><I className="h-5 w-5" /></span>
                <div><p className="font-display text-lg font-bold">{t}</p><p className="text-sm text-emerald-950/70">{d}</p></div>
              </div>
            ))}
          </div>
          <a href={PHONE_HREF} data-testid="booking-phone-link" className="mt-10 inline-flex items-center gap-2 text-sm font-semibold text-emerald-700 hover:text-emerald-900">
            <Phone className="h-4 w-4" /> Rather call now? {PHONE}
          </a>
        </Reveal>

        <Reveal delay={0.1} className="min-w-0 lg:col-span-7">
          <div className="rounded-[2rem] border border-emerald-900/10 bg-[#EAF3FF] p-5 sm:p-8" data-testid="booking-widget">
            {done ? <Done label={done} onReset={reset} /> : slot ? (
              <form onSubmit={submit} className="space-y-4" data-testid="booking-form">
                <button type="button" onClick={() => setSlot(null)} data-testid="booking-back-btn" className="inline-flex items-center gap-1.5 text-sm font-semibold text-emerald-700"><ArrowLeft className="h-4 w-4" /> Change time</button>
                <div className="rounded-2xl bg-[#0B2A6F] p-4 text-white" data-testid="booking-selected-slot">
                  <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-lime-300">Your call</p>
                  <p className="mt-1 font-display text-xl font-bold">{current?.weekday}, {current?.label} · {slot.time} ET</p>
                </div>
                <div className="grid gap-4 sm:grid-cols-2">
                  <Field label="Name" testid="booking-name-input" required value={form.name} onChange={set("name")} placeholder="Jane Doe" />
                  <Field label="Business" testid="booking-business-input" value={form.business} onChange={set("business")} placeholder="Main Street Cafe" />
                  <Field label="Email" testid="booking-email-input" type="email" required value={form.email} onChange={set("email")} placeholder="you@business.com" />
                  <Field label="Phone" testid="booking-phone-input" type="tel" required minLength={7} value={form.phone} onChange={set("phone")} placeholder="(555) 000-0000" />
                </div>
                <Field label="Anything we should know? (optional)" testid="booking-message-input" value={form.message} onChange={set("message")} placeholder="What do you sell, and how do you take payments today?" />
                <button disabled={busy} data-testid="booking-submit-btn" className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-lime-400 py-4 text-sm font-bold text-[#050B1F] transition-[transform,background-color] hover:-translate-y-0.5 hover:bg-lime-300 disabled:opacity-60">
                  {busy && <Loader2 className="h-4 w-4 animate-spin" />} Confirm my call
                </button>
              </form>
            ) : (
              <div className="space-y-6">
                <div className="flex items-center justify-between">
                  <p className="font-display text-xl font-bold">Choose a day</p>
                  <span className="font-mono text-[11px] uppercase tracking-[0.15em] text-emerald-700">Eastern Time</span>
                </div>
                {days === null && <Loader2 className="mx-auto h-6 w-6 animate-spin text-emerald-700" />}
                {days?.length === 0 && <p className="text-sm text-emerald-950/75" data-testid="booking-empty">No open times right now. Call us at {PHONE}.</p>}
                {days?.length > 0 && (
                  <>
                    <DayPicker days={days} day={day} onPick={(i) => { setDay(i); setSlot(null); }} />
                    <div>
                      <p className="mb-3 font-display text-xl font-bold">Choose a time</p>
                      <TimeGrid slots={current?.slots ?? []} slot={slot} onPick={setSlot} />
                    </div>
                  </>
                )}
              </div>
            )}
          </div>
        </Reveal>
      </div>
    </section>
  );
};
